import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requestsService, usersService } from '../services/apiServices';
import { Request } from '../types';

// Query keys
export const REQUEST_KEYS = {
  all: ['requests'] as const,
  list: (params?: any) => [...REQUEST_KEYS.all, 'list', params] as const,
  detail: (id: number) => [...REQUEST_KEYS.all, 'detail', id] as const,
  stats: () => [...REQUEST_KEYS.all, 'stats'] as const,
};

// Fetch all requests with optional filters
export const useRequests = (params?: any) => {
  return useQuery({
    queryKey: REQUEST_KEYS.list(params),
    queryFn: () => requestsService.getAll(params),
  });
};

// Fetch single request by ID
export const useRequest = (id: number) => {
  return useQuery({
    queryKey: REQUEST_KEYS.detail(id),
    queryFn: () => requestsService.getById(id),
    enabled: !!id,
  });
};

// Fetch request statistics
export const useRequestStats = () => {
  return useQuery({
    queryKey: REQUEST_KEYS.stats(),
    queryFn: () => requestsService.getStats(),
  });
};

// Create new request
export const useCreateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Request>) => requestsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.all });
    },
  });
};

// Update request
export const useUpdateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Request> }) =>
      requestsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.all });
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.detail(variables.id) });
    },
  });
};

// Assign request to user
export const useAssignRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) =>
      requestsService.assign(id, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.all });
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.detail(variables.id) });
    },
  });
};

// Mark request as in progress
export const useMarkInProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => requestsService.markInProgress(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.all });
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.detail(id) });
    },
  });
};

// Complete request
export const useCompleteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => requestsService.complete(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.all });
      queryClient.invalidateQueries({ queryKey: REQUEST_KEYS.detail(variables.id) });
    },
  });
};

// Fetch all users for assignment
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
  });
};
